import { useEffect, useState, useCallback } from "react";
import { Loader2, Layers, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const JUST_MIN = 30;

/** Semáforo por score: rojo >= 90 · ámbar 70-89 · gris < 70. */
export const colorScore = (s) => (Number(s) >= 90 ? "rojo" : Number(s) >= 70 ? "ambar" : "gris");

const CHIPS = [
  { k: "rojo", label: "Relevantes (90 o más)", cls: "border-red-300 bg-red-50 text-red-800", testid: "semaforo-rojo" },
  { k: "ambar", label: "Parecido medio (70-89)", cls: "border-amber-300 bg-amber-50 text-amber-800", testid: "semaforo-ambar" },
  { k: "gris", label: "Parecido bajo (menos de 70)", cls: "border-slate-300 bg-slate-100 text-slate-600", testid: "semaforo-gris" },
];

export function SemaforoScore({ matches }) {
  const pend = matches.filter((m) => m.estatus === "pendiente");
  const n = { rojo: 0, ambar: 0, gris: 0 };
  pend.forEach((m) => { n[colorScore(m.score)] += 1; });
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="semaforo-score">
      <span className="text-xs text-slate-500">Pendientes por similitud:</span>
      {CHIPS.map((c) => (
        <span key={c.k} data-testid={c.testid}
          className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${c.cls}`}>
          {c.label}: {n[c.k]}
        </span>
      ))}
    </div>
  );
}

export default function DescarteUmbralBloque({ matches = [], onDone }) {
  const { user } = useAuth();
  const esOficial = user?.role === "oficial_cumplimiento";
  const [open, setOpen] = useState(false);
  const [prev, setPrev] = useState(null);
  const [just, setJust] = useState("");
  const [enviando, setEnviando] = useState(false);

  const cargar = useCallback(async () => {
    if (!esOficial) return;
    try { setPrev((await api.get("/pld/descarte-umbral/preview")).data); }
    catch { setPrev(null); }
  }, [esOficial]);

  useEffect(() => { cargar(); }, [cargar, matches.length]);

  const confirmar = async () => {
    setEnviando(true);
    try {
      const { data } = await api.post("/pld/descarte-umbral", { justificacion: just });
      toast.success(`${data.descartados} coincidencia(s) descartada(s) — folio ${data.folio}`);
      setOpen(false); setJust("");
      await cargar();
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo descartar en bloque"); }
    finally { setEnviando(false); }
  };

  return (
    <div className="mb-4 rounded-md border border-slate-200 bg-white px-3 py-2.5" data-testid="descarte-umbral-bloque">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SemaforoScore matches={matches} />
        {esOficial && (
          <Button data-testid="descarte-umbral-abrir-btn" size="sm" variant="outline"
            disabled={!prev?.total} onClick={() => setOpen(true)} className="text-xs">
            <Layers className="mr-1.5 h-4 w-4" />
            Descartar en bloque por umbral{prev?.total ? ` (${prev.total})` : ""}
          </Button>
        )}
      </div>
      {esOficial && prev?.total > 0 && (
        <p className="mt-2 text-xs text-slate-500" data-testid="descarte-umbral-nota">
          Hay <b>{prev.total}</b> coincidencia(s) pendiente(s) con similitud menor a {prev.umbral}% en{" "}
          {prev.personas} persona(s). No retienen operaciones; puedes resolverlas en un solo acto con justificación y bitácora.
        </p>
      )}

      <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="descarte-umbral-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Descartar en bloque por umbral</DialogTitle>
            <DialogDescription className="text-xs">
              Solo coincidencias <b>pendientes</b> con similitud menor a {prev?.umbral ?? 90}%. Las de {prev?.umbral ?? 90}% o más,
              las de nivel exacta/alta y las ya confirmadas <b>no se tocan</b>. Nada se borra: quedan como «Descartada» con tu justificación.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-3 gap-2" data-testid="descarte-umbral-preview">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
                <p className="text-[11px] text-slate-400">Coincidencias</p>
                <p className="text-lg font-semibold" data-testid="descarte-umbral-total">{prev?.total ?? 0}</p>
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
                <p className="text-[11px] text-slate-400">Personas</p>
                <p className="text-lg font-semibold" data-testid="descarte-umbral-personas">{prev?.personas ?? 0}</p>
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
                <p className="text-[11px] text-slate-400">Umbral</p>
                <p className="text-lg font-semibold">{prev?.umbral ?? 90}%</p>
              </div>
            </div>
            {prev?.por_lista && (
              <div className="rounded-md border border-slate-200 p-2 text-xs" data-testid="descarte-umbral-por-lista">
                {Object.entries(prev.por_lista).slice(0, 6).map(([k, v]) => (
                  <div key={k} className="flex justify-between py-0.5"><span className="text-slate-600">{k}</span><span className="font-semibold">{v}</span></div>
                ))}
              </div>
            )}
            <div>
              <Label className="mb-1 block text-xs">Justificación (mínimo {JUST_MIN} caracteres)</Label>
              <Textarea data-testid="descarte-umbral-justificacion" rows={4} value={just}
                onChange={(e) => setJust(e.target.value)} placeholder={prev?.texto_sugerido || ""} />
              <p className="mt-1 text-[11px] text-slate-400">{just.trim().length}/{JUST_MIN}</p>
            </div>
            <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              Queda tu nombre, la fecha, el criterio y la justificación en la bitácora de la entidad, con folio de lote.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" data-testid="descarte-umbral-cancelar" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="descarte-umbral-confirmar" className="bg-primary hover:bg-sidebar-hover"
              disabled={enviando || just.trim().length < JUST_MIN || !prev?.total} onClick={confirmar}>
              {enviando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Descartar {prev?.total ?? 0} coincidencia(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
